import api from "./api"
import { RegistrarLogErro } from "./clientefiel"

const authHeader = (authorization: string) => ({
    "Content-Type": "application/json",
    Authorization: authorization,
})

export interface ErroApi {
    retornoErro: number
    mensagem: string
}

type RespostaRaw<T> = T & { codErro?: number; mensagem?: string }

function tratamentoResponse<T>(response: { data: RespostaRaw<T> }, chamada: string): T | ErroApi {
    const { data } = response
    if (data && (data as { codErro?: number }).codErro !== undefined && data.codErro! > 0) {
        return { retornoErro: data.codErro!, mensagem: data.mensagem ?? "" }
    }
    if (typeof data === "string" && data !== "") {
        return {
            retornoErro: 1,
            mensagem: `${chamada} - Erro inesperado, entre em contato com os responsáveis.`,
        }
    }
    return data
}

function erroParaRespostaRaw(error: unknown): RespostaRaw<never> {
    const axiosError = error as { message?: string; response?: { data?: unknown } }
    const data = axiosError.response?.data
    if (
        typeof data === "object" &&
        data !== null &&
        "codErro" in data &&
        (data as { codErro: number }).codErro > 0
    ) {
        const d = data as { codErro: number; mensagem?: string }
        return { codErro: d.codErro, mensagem: d.mensagem } as RespostaRaw<never>
    }
    if (axiosError.message === "Network Error") {
        return { codErro: 1, mensagem: "Verifique sua conexão!" } as RespostaRaw<never>
    }
    return { codErro: 1, mensagem: axiosError.message || "Erro desconhecido" } as RespostaRaw<never>
}

// ─── Modelos ─────────────────────────────────────────────────────────────────

/** Parceiro homologado a consumir a nossa API, ou praça de entrega que nós consumimos. */
export interface Integrador {
    id: number
    nome: string
    tipo: string
    /** true = praça de entrega (nós chamamos ele). false = PDV/hub (ele nos chama). */
    logistica: boolean
    contatoEmail: string | null
    site: string | null
    webhookUrl: string | null
    appIdParceiro: string | null
    observacao: string | null
    ativo: boolean
    quantidadeLojas?: number
}

export interface NovoIntegrador {
    nome: string
    contatoEmail?: string
    site?: string
    webhookUrl?: string
    observacao?: string
    logistica: boolean
    appIdParceiro?: string
}

/** Credenciais que geramos para a loja usar num PDV. */
export interface ConexaoPdv {
    id: number
    integradorId: number
    integradorNome: string
    clientId: string
    clientSecret: string
    merchantId: string
    webhookUrl: string | null
    ativo: boolean
}

export interface IntegradorResumo {
    id: number
    nome: string
}

export interface Conexoes {
    urlBase: string
    appId: string
    estabelecimentoNome: string | null
    estabelecimentoCnpj: string | null
    credenciais: ConexaoPdv[]
    disponiveis: IntegradorResumo[]
}

/** Credenciais que a praça de entrega nos forneceu. Direção oposta à do PDV. */
export interface CredencialLogistica {
    id: number
    integradorId: number
    integradorNome: string
    baseUrl: string
    clientId: string
    clientSecret: string
    merchantId: string
    automatico: boolean
    ativo: boolean
}

export interface Logistica {
    credencial?: CredencialLogistica
    disponiveis: IntegradorResumo[]
}

export interface PedidoParaTeste {
    id: number
    localizador: string | null
    dataRealizacao: string | null
    status: number | null
    valorTotal: number | null
    uuid: string | null
}

export interface PassoSimulacao {
    titulo: string
    descricao: string
    sucesso: boolean
    http: number | null
    payload: string | null
    erro: string | null
}

export interface ResultadoSimulacao {
    sucesso: boolean
    orderUuid: string
    passos: PassoSimulacao[]
}

// ─── Integradores (administrativo) ───────────────────────────────────────────

export const listarIntegradores = async function (
    authorization: string
): Promise<Integrador[] | ErroApi> {
    try {
        const response = await api
            .get<RespostaRaw<Integrador[]>>("opendelivery-admin/integradores", {
                headers: authHeader(authorization),
            })
            .catch(error => ({ data: erroParaRespostaRaw(error) }))
        return tratamentoResponse<Integrador[]>(response, "listarIntegradores")
    } catch (error) {
        RegistrarLogErro(error, "listarIntegradores", "")
        return { retornoErro: 1, mensagem: (error as Error).message }
    }
}

export const criarIntegrador = async function (
    authorization: string,
    integrador: NovoIntegrador
): Promise<Integrador | ErroApi> {
    try {
        const response = await api
            .post<RespostaRaw<Integrador>>("opendelivery-admin/integradores", integrador, {
                headers: authHeader(authorization),
            })
            .catch(error => ({ data: erroParaRespostaRaw(error) }))
        return tratamentoResponse<Integrador>(response, "criarIntegrador")
    } catch (error) {
        RegistrarLogErro(error, "criarIntegrador", JSON.stringify(integrador))
        return { retornoErro: 1, mensagem: (error as Error).message }
    }
}

/** Edita os dados cadastrais. O tipo (PDV x logística) não muda depois de criado. */
export const editarIntegrador = async function (
    authorization: string,
    id: number,
    integrador: Omit<NovoIntegrador, "logistica">
): Promise<Integrador | ErroApi> {
    try {
        const response = await api
            .post<RespostaRaw<Integrador>>(`opendelivery-admin/integradores/${id}`, integrador, {
                headers: authHeader(authorization),
            })
            .catch(error => ({ data: erroParaRespostaRaw(error) }))
        return tratamentoResponse<Integrador>(response, "editarIntegrador")
    } catch (error) {
        RegistrarLogErro(error, "editarIntegrador", JSON.stringify(integrador))
        return { retornoErro: 1, mensagem: (error as Error).message }
    }
}

export const alternarIntegrador = async function (
    authorization: string,
    id: number
): Promise<Integrador | ErroApi> {
    try {
        const response = await api
            .post<RespostaRaw<Integrador>>(
                `opendelivery-admin/integradores/${id}/alternar-ativo`,
                {},
                { headers: authHeader(authorization) }
            )
            .catch(error => ({ data: erroParaRespostaRaw(error) }))
        return tratamentoResponse<Integrador>(response, "alternarIntegrador")
    } catch (error) {
        RegistrarLogErro(error, "alternarIntegrador", String(id))
        return { retornoErro: 1, mensagem: (error as Error).message }
    }
}

// ─── Conexões da loja com PDVs ───────────────────────────────────────────────

export const obterConexoes = async function (authorization: string): Promise<Conexoes | ErroApi> {
    try {
        const response = await api
            .get<RespostaRaw<Conexoes>>("opendelivery-admin/conexoes", {
                headers: authHeader(authorization),
            })
            .catch(error => ({ data: erroParaRespostaRaw(error) }))
        return tratamentoResponse<Conexoes>(response, "obterConexoes")
    } catch (error) {
        RegistrarLogErro(error, "obterConexoes", "")
        return { retornoErro: 1, mensagem: (error as Error).message }
    }
}

export const conectarIntegrador = async function (
    authorization: string,
    integradorId: number
): Promise<Conexoes | ErroApi> {
    try {
        const response = await api
            .post<RespostaRaw<Conexoes>>(
                "opendelivery-admin/conexoes",
                { integradorId },
                { headers: authHeader(authorization) }
            )
            .catch(error => ({ data: erroParaRespostaRaw(error) }))
        return tratamentoResponse<Conexoes>(response, "conectarIntegrador")
    } catch (error) {
        RegistrarLogErro(error, "conectarIntegrador", String(integradorId))
        return { retornoErro: 1, mensagem: (error as Error).message }
    }
}

export const regerarSecret = async function (
    authorization: string,
    credencialId: number
): Promise<Conexoes | ErroApi> {
    try {
        const response = await api
            .post<RespostaRaw<Conexoes>>(
                `opendelivery-admin/conexoes/${credencialId}/regerar-secret`,
                {},
                { headers: authHeader(authorization) }
            )
            .catch(error => ({ data: erroParaRespostaRaw(error) }))
        return tratamentoResponse<Conexoes>(response, "regerarSecret")
    } catch (error) {
        RegistrarLogErro(error, "regerarSecret", String(credencialId))
        return { retornoErro: 1, mensagem: (error as Error).message }
    }
}

export const alternarConexao = async function (
    authorization: string,
    credencialId: number
): Promise<Conexoes | ErroApi> {
    try {
        const response = await api
            .post<RespostaRaw<Conexoes>>(
                `opendelivery-admin/conexoes/${credencialId}/alternar-ativo`,
                {},
                { headers: authHeader(authorization) }
            )
            .catch(error => ({ data: erroParaRespostaRaw(error) }))
        return tratamentoResponse<Conexoes>(response, "alternarConexao")
    } catch (error) {
        RegistrarLogErro(error, "alternarConexao", String(credencialId))
        return { retornoErro: 1, mensagem: (error as Error).message }
    }
}

// ─── Logística ───────────────────────────────────────────────────────────────

export const obterLogistica = async function (authorization: string): Promise<Logistica | ErroApi> {
    try {
        const response = await api
            .get<RespostaRaw<Logistica>>("opendelivery-admin/logistica", {
                headers: authHeader(authorization),
            })
            .catch(error => ({ data: erroParaRespostaRaw(error) }))
        return tratamentoResponse<Logistica>(response, "obterLogistica")
    } catch (error) {
        RegistrarLogErro(error, "obterLogistica", "")
        return { retornoErro: 1, mensagem: (error as Error).message }
    }
}

/** merchantId estável da loja para informar no cadastro da praça de entrega. Não grava nada. */
export const gerarMerchantIdLogistica = async function (
    authorization: string
): Promise<{ merchantId: string } | ErroApi> {
    try {
        const response = await api
            .get<RespostaRaw<{ merchantId: string }>>("opendelivery-admin/logistica/merchant-id", {
                headers: authHeader(authorization),
            })
            .catch(error => ({ data: erroParaRespostaRaw(error) }))
        return tratamentoResponse<{ merchantId: string }>(response, "gerarMerchantIdLogistica")
    } catch (error) {
        RegistrarLogErro(error, "gerarMerchantIdLogistica", "")
        return { retornoErro: 1, mensagem: (error as Error).message }
    }
}

export const salvarLogistica = async function (
    authorization: string,
    dados: {
        integradorId?: number
        baseUrl: string
        clientId: string
        clientSecret: string
        merchantId: string
        automatico: boolean
    }
): Promise<Logistica | ErroApi> {
    try {
        const response = await api
            .post<RespostaRaw<Logistica>>("opendelivery-admin/logistica", dados, {
                headers: authHeader(authorization),
            })
            .catch(error => ({ data: erroParaRespostaRaw(error) }))
        return tratamentoResponse<Logistica>(response, "salvarLogistica")
    } catch (error) {
        RegistrarLogErro(error, "salvarLogistica", JSON.stringify(dados))
        return { retornoErro: 1, mensagem: (error as Error).message }
    }
}

// ─── Simulador ───────────────────────────────────────────────────────────────

export const listarPedidosParaTeste = async function (
    authorization: string
): Promise<PedidoParaTeste[] | ErroApi> {
    try {
        const response = await api
            .get<RespostaRaw<PedidoParaTeste[]>>("opendelivery-admin/simulador/pedidos", {
                headers: authHeader(authorization),
            })
            .catch(error => ({ data: erroParaRespostaRaw(error) }))
        return tratamentoResponse<PedidoParaTeste[]>(response, "listarPedidosParaTeste")
    } catch (error) {
        RegistrarLogErro(error, "listarPedidosParaTeste", "")
        return { retornoErro: 1, mensagem: (error as Error).message }
    }
}

export const executarSimulacao = async function (
    authorization: string,
    dados: { credencialId: number; pedidoId: number; confirmar: boolean }
): Promise<ResultadoSimulacao | ErroApi> {
    try {
        const response = await api
            .post<RespostaRaw<ResultadoSimulacao>>("opendelivery-admin/simulador/executar", dados, {
                headers: authHeader(authorization),
            })
            .catch(error => ({ data: erroParaRespostaRaw(error) }))
        return tratamentoResponse<ResultadoSimulacao>(response, "executarSimulacao")
    } catch (error) {
        RegistrarLogErro(error, "executarSimulacao", JSON.stringify(dados))
        return { retornoErro: 1, mensagem: (error as Error).message }
    }
}
